// Booking flow — internal /book page hosts the Pabau widget in an iframe so
// the flow feels native to the site. The raw Pabau URL below is used only by
// the /book page (embed src + fallback link) — nothing else references it
// directly.
export const BOOKING_URL = "/book";
export const PABAU_BOOKING_URL = "https://partner-uk.pabau.com/online-bookings/ashlanclinic";

export const STAFF_LOGIN_URL = "https://portal.pabau.com/portal";
export const MARKETING_WORKSPACE_URL = "https://postyourcontent.com/app";

// Central map so we can differentiate per-Therapist later without touching pages.
// For now all point to the same /book page; Pabau will support per-Therapist
// deep-links soon — swap the values below and everything downstream picks it up.
export const THERAPIST_BOOKING_URLS = {
  "glenn-robinson": BOOKING_URL,
  "teshea-pitt": BOOKING_URL,
  "holly-paiva": BOOKING_URL,
};

// H4 — Trust counter displayed on the home hero. Update this number as the
// clinic grows. (When you switch booking platforms, we can wire this to a
// live session count instead.)
export const SESSIONS_DELIVERED = 30000;

export const BUSINESS = {
  name: "Ashlan Clinic",
  address1: "48 Cedar Avenue",
  address2: "Hamilton HM11",
  country: "Bermuda",
  phone: "(441) 292-6830",
  phoneHref: "tel:+14412926830",
  email: "info@ashlanclinic.com",
  website: "www.ashlanclinic.com",
  tagline: "#ComeSeeUs",
};

export const INSURANCE_PROVIDERS = [
  { name: "CG Insurance", url: "https://www.cgcoralisle.com/" },
  { name: "Allshores", url: "https://allshores.com/about-allshores" },
];

export const ASSETS = {
  ashlanLogo:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/rskp9grq_ashlan_logo.png",
  therabodyLogo:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/xfjhhtyd_therabody_logo.png",
  heroClinical:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/hyr0rc80_HERO%20IMAGE%20-%20MASSAGE%203.png",
  cupping:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/d10ww7qm_DEEP%20TISSUE%20MASSAGE%207.png",
  reflexology:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/xhehak4l_REFLEXOLOGY%2027.png",
  theragunMiniPlus:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/1fo2g5ly_Theragun-Mini-Plus-Hero-Image-1.webp",
  jetbootsPrime:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/5g9evqum_JetBoots-2-New.webp",
  smartGoggles:
    "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/paiof3df_Smart-Goggles-2-Product-img-1_fafd37aa-5e3c-4067-ac7d-7ea5e39a7fc5_1.webp",
  theraFaceMaskGlo:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Theraface-Mask-Glo-PLP-Thumbnail-1.webp?v=1759992226",
  theragunMiniGen3:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Mini-Gen-3-Black-PLP-Thumbnail-1.png?v=1741752124",
  sleepMask:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Sleep-Mask-PLP-Thumbnail-1.png?v=1740016296",
  theraFaceDepuffingWand:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Theraface-Depuffing-Wand-PLP-Thumbnail-1-New.webp?v=1743488017",
  jetbootsProPlus:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Jetboots-Pro-Plus-PLP-Thumbnail-1.png?v=1739878412",
  thermBackLed:
    "https://cdn.shopify.com/s/files/1/0671/4097/6867/files/Therm-Back-LED-PLP-Thumbnail-1.webp?v=1744162086",
};

export const THERAPISTS = [
  {
    slug: "glenn-robinson",
    name: "Glenn Robinson",
    credentials: "CMT, NMT",
    role: "Lead Clinical Massage Therapist",
    bio: "Glenn leads the clinic with over two decades of treatment experience. He specialises in neuromuscular therapy, structural integration and evidence-based protocols for complex pain presentations. His approach is methodical: thorough assessment, identification of the underlying driver, and a treatment plan that resolves rather than masks symptoms.",
    specialties: [
      "Neuromuscular Therapy",
      "Chronic Pain",
      "Postural Correction",
      "Sports Rehabilitation",
      "Sciatica & Disc Referral",
    ],
    photo:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/ay1o3o90_glenn%20website%20photo.avif",
    bookingUrl: "/book",
  },
  {
    slug: "teshea-pitt",
    name: "Teshea Pitt",
    credentials: "RMT, Dipl SMT",
    role: "Clinical Massage Therapist & Shiatsu Practitioner",
    bio: "Teshea blends a rigorous clinical foundation with the precision of traditional Shiatsu. Her work is particularly suited to clients carrying long-term tension, recurring headaches, and stress-related musculoskeletal patterns. Sessions are deliberate, focused and built around measurable progress.",
    specialties: [
      "Shiatsu",
      "Headaches & Migraine",
      "Stress & Tension",
      "Neck & Shoulder Restriction",
      "Pregnancy Discomfort",
    ],
    photo:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/ccmh2bvb_teshea%20website%20photo.avif",
    bookingUrl: "/book",
  },
  {
    slug: "holly-paiva",
    name: "Holly Paiva",
    credentials: "M.A. Sleep Studies",
    role: "Reflexologist & Sleep Wellness Specialist",
    bio: "Holly bridges reflexology with applied sleep science. Her sessions support clients struggling with disrupted sleep, nervous system overload and recovery from extended stress periods. Each treatment is informed by current research in sleep physiology and parasympathetic regulation.",
    specialties: [
      "Reflexology",
      "Sleep Optimisation",
      "Nervous System Regulation",
      "Recovery Protocols",
      "Pregnancy Support",
    ],
    photo:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/sgaz83zo_Holly%20Website%20photo.avif",
    bookingUrl: "/book",
  },
];

export const CONDITIONS = [
  { name: "Back Pain", icon: "Activity" },
  { name: "Neck Pain", icon: "User" },
  { name: "Shoulder Pain", icon: "MoveDiagonal" },
  { name: "Sciatica", icon: "Zap" },
  { name: "Sports Injuries", icon: "Dumbbell" },
  { name: "Headaches", icon: "Brain" },
  { name: "Pregnancy Discomfort", icon: "HeartHandshake" },
  { name: "Stress & Tension", icon: "Wind" },
];

export const PROCESS_STEPS = [
  { n: "01", t: "Comprehensive Intake", d: "Detailed history, lifestyle and movement screen." },
  { n: "02", t: "Clinical Assessment", d: "Postural, orthopaedic and palpation findings recorded." },
  { n: "03", t: "Root Cause Identification", d: "We isolate the underlying driver, not the referred symptom." },
  { n: "04", t: "Treatment Plan", d: "Evidence-based protocol with clear milestones." },
  { n: "05", t: "Hands-On Treatment", d: "Targeted manual therapy delivered with precision." },
  { n: "06", t: "Home Programming", d: "Specific corrective work between sessions." },
  { n: "07", t: "Outcome Review", d: "Re-assessment, progression and lasting transformation." },
];

export const WHAT_WE_DO = [
  {
    icon: "Stethoscope",
    title: "Clinical Massage Therapy",
    body: "Evidence-based manual therapy targeting the underlying driver of pain and dysfunction.",
    link: "/services",
  },
  {
    icon: "Leaf",
    title: "Holistic Wellness",
    body: "Reflexology, Shiatsu and sleep-focused protocols supporting nervous system regulation.",
    link: "/services",
  },
  {
    icon: "Cpu",
    title: "Therabody Products",
    body: "Officially curated recovery technology, professionally fitted to your treatment plan.",
    link: "/therabody",
  },
];

export const SERVICES = [
  {
    category: "Pain & Rehabilitation",
    icon: "Activity",
    items: [
      "Chronic Back & Neck Pain",
      "Sciatica",
      "Frozen Shoulder",
      "Tendinopathy & Soft Tissue Injury",
    ],
    description:
      "Targeted clinical protocols for long-standing pain. Thorough assessment, structural correction and measurable outcomes.",
  },
  {
    category: "Sports Performance",
    icon: "Dumbbell",
    items: [
      "Pre & Post-Event Preparation",
      "Sports Injury Recovery",
      "Mobility & Range Restoration",
      "Athlete Maintenance",
    ],
    description:
      "Designed for athletes and active clients. Pre-conditioning, performance support, and recovery, grounded in clinical reasoning.",
  },
  {
    category: "Wellness & Recovery",
    icon: "Leaf",
    items: [
      "Reflexology",
      "Stress & Tension Relief",
      "Sleep Wellness Sessions",
      "Nervous System Regulation",
    ],
    description:
      "Evidence-informed, restorative and quietly transformative.",
  },
  {
    category: "Women's Care",
    icon: "HeartHandshake",
    items: [
      "Pregnancy Massage",
      "Post-Natal Recovery",
      "Pelvic & Lower Back Support",
      "Stress Reduction",
    ],
    description:
      "A dedicated delivery of care for women at every life stage, delivered by experienced therapists.",
  },
  {
    category: "Corporate Wellness",
    icon: "Briefcase",
    items: [
      "On-Site Therapy Programmes",
      "Executive Recovery",
      "Postural & Desk Strain",
      "Team Wellbeing Packages",
    ],
    description:
      "Workplace programmes that reduce absenteeism and improve performance — clinical, professional, measurable.",
  },
];

export const THERABODY_PRODUCTS = [
  {
    slug: "theragun-mini-plus",
    name: "Theragun Mini Plus",
    image: ASSETS.theragunMiniPlus,
    description:
      "Theragun Mini Plus is the only heated, travel-sized massage gun built to activate muscles, ease soreness and support recovery — delivering results faster than percussive massage alone. Lightweight and pocketable, it fits a locker, a kit bag, or a desk drawer.",
    benefits: [
      "Heat plus percussive therapy in one device",
      "Travel-sized, pocketable design",
      "Quiet, professional-grade motor",
      "Multiple force levels for targeted work",
    ],
    idealFor: [
      "Pre-training warm-ups and on the sidelines",
      "Athletes maintaining recovery on the road",
      "Clients prescribed home maintenance between sessions",
    ],
  },
  {
    slug: "smartgoggles",
    name: "SmartGoggles",
    image: ASSETS.smartGoggles,
    description:
      "The redesigned SmartGoggles 2nd Gen features a 100% blackout design with enhanced comfort. Combining compression massage, gentle heat and vibration therapies, it can support relaxation, ease of tension headaches and improved sleep quality.",
    benefits: [
      "Compression massage, heat and vibration in one",
      "100% blackout for true rest",
      "Guided breathing and biometric feedback",
      "Lightweight, travel-friendly form",
    ],
    idealFor: [
      "Sleep wellness clients",
      "Frequent screen users and travellers",
      "Anyone managing chronic stress or tension headaches",
    ],
  },
  {
    slug: "jetboots-prime",
    name: "JetBoots Prime",
    image: ASSETS.jetbootsPrime,
    description:
      "JetBoots Prime are fully wireless pneumatic compression boots designed to reduce soreness, support faster recovery and help maintain performance. Their lightweight, foldable design lets you take recovery wherever the day takes you.",
    benefits: [
      "Fully wireless — no tethered pump",
      "Lightweight, foldable for travel",
      "Pneumatic compression that flushes the legs",
      "Multiple intensity levels and zones",
    ],
    idealFor: [
      "Endurance athletes and runners",
      "Clients recovering from lower-limb injury",
      "Heavy legs, post-flight swelling and long days on your feet",
    ],
  },
  {
    slug: "jetboots-pro-plus",
    name: "JetBoots PRO Plus",
    image: ASSETS.jetbootsProPlus,
    description:
      "JetBoots PRO Plus are the first wireless leg compression boots to combine pneumatic compression, vibration and infrared LED light — three therapies working together to target muscle soreness, ease swelling and support the body through hard training blocks.",
    benefits: [
      "Pneumatic compression, vibration and infrared LED in one boot",
      "Fully wireless, fully portable",
      "Multiple sizes and intensity zones",
      "Designed for demanding training schedules",
    ],
    idealFor: [
      "Competitive and endurance athletes",
      "Clients in heavy training blocks or back-to-back events",
      "Anyone seeking deeper, layered lower-limb recovery",
    ],
  },
  {
    slug: "theragun-mini-gen-3",
    name: "Theragun Mini (3rd Gen)",
    image: ASSETS.theragunMiniGen3,
    description:
      "Theragun Mini 3rd Gen is the smallest, lightest Theragun yet — an ultra-portable percussive device that slips into a bag, a desk drawer or a glove box. It's a simple way to ease everyday aches, soften muscle tension and support recovery between sessions.",
    benefits: [
      "Smallest, lightest Theragun in the range",
      "Quiet, professional-grade percussive motor",
      "Three speed settings for targeted work",
      "Travel-ready, pocketable design",
    ],
    idealFor: [
      "Clients prescribed home maintenance between visits",
      "Frequent travellers and busy professionals",
      "Anyone managing day-to-day muscle tightness",
    ],
  },
  {
    slug: "sleep-mask",
    name: "SleepMask",
    image: ASSETS.sleepMask,
    description:
      "An ultra-comfortable, 100% blackout eye mask paired with three quiet vibration patterns that can help you settle, slow down and drift off more easily. Designed to support deeper rest for clients managing stress, screen fatigue or disrupted sleep.",
    benefits: [
      "100% blackout for true rest",
      "Three soothing vibration patterns",
      "Lightweight, head-friendly fit",
      "Rechargeable, travel-friendly",
    ],
    idealFor: [
      "Clients with disrupted or restless sleep",
      "Shift workers and frequent travellers",
      "Anyone winding down from a high-stress day",
    ],
  },
  {
    slug: "theraface-mask-glo",
    name: "TheraFace Mask Glo",
    image: ASSETS.theraFaceMaskGlo,
    description:
      "TheraFace Mask Glo pairs 504 red, infrared and blue LED lights with a gentle scalp massage to support circulation and ease facial tension in around 12 minutes a day. A considered, at-home complement to the recovery work we do in clinic.",
    benefits: [
      "504 red, infrared and blue LEDs",
      "Gentle scalp massage element",
      "Short, daily 12-minute treatments",
      "Hands-free, easy-to-wear design",
    ],
    idealFor: [
      "Clients adding LED therapy to their home routine",
      "Anyone managing facial tension or jaw tightness",
      "Recovery-minded clients focused on the face and scalp",
    ],
  },
  {
    slug: "theraface-depuffing-wand",
    name: "TheraFace Depuffing Wand",
    image: ASSETS.theraFaceDepuffingWand,
    description:
      "The TheraFace Depuffing Wand combines cold and heat therapy with a contoured tip designed for lymphatic-style massage around the eyes and face. A gentle, at-home tool that can support how you feel after travel, long days or restless sleep.",
    benefits: [
      "Cold and heat therapy in one device",
      "Contoured tip for eye and face work",
      "Designed to support lymphatic-style massage",
      "Compact, travel-friendly form",
    ],
    idealFor: [
      "Post-flight and post-event recovery",
      "Clients managing puffiness or facial tension",
      "Anyone adding a calming step to their morning routine",
    ],
  },
  {
    slug: "thermback-led",
    name: "ThermBack LED",
    image: ASSETS.thermBackLed,
    description:
      "ThermBack LED layers four therapies — LED, far-infrared, heat and vibration — across the lower back. Designed to ease everyday low-back discomfort, support mobility and complement the hands-on work we do in clinic.",
    benefits: [
      "LED, far-infrared, heat and vibration in one wearable",
      "Customisable treatment modes",
      "Targets the lower back specifically",
      "Hands-free, sit-down or lie-down use",
    ],
    idealFor: [
      "Clients managing lower-back tension or stiffness",
      "Desk-based workers and long-haul travellers",
      "Anyone looking to extend clinic care at home",
    ],
  },
];

export const SEED_TESTIMONIALS = [
  {
    id: "seed-1",
    name: "Margaret H.",
    location: "Hamilton, Bermuda",
    quote:
      "After years of being told to simply live with the pain, the team at Ashlan Clinic found the actual cause within a single appointment. Three months later I am genuinely pain-free.",
  },
  {
    id: "seed-2",
    name: "David R.",
    location: "Paget",
    quote:
      "Glenn's approach is methodical and precise. There is no fluff, no spa nonsense — just real clinical work that produced measurable results for my chronic sciatica.",
  },
  {
    id: "seed-3",
    name: "Sarah L.",
    location: "Smith's",
    quote:
      "Teshea's Shiatsu work has changed how I sleep, how I move and how I show up at work. Easily the most professional treatment I have ever received in Bermuda.",
  },
];
